import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { FILTER_INPUT_AUTOFILL_ATTRS, unlockFilterInputAfterFocus } from '../utils/filterInputAutofill';
import { UI_Z_INDEX } from '../utils/uiZIndex';

export type AppSelectOption = {
  value: string;
  label: string;
  /** Optional second line under the label (e.g. contact · id). */
  description?: string;
  /** Extra searchable text (not shown); e.g. Bestellnummer, Baustellenkontakt. */
  searchText?: string;
  icon?: React.ReactNode;
  group?: string;
};

const ICON_ONLY_PANEL_WIDTH = 264;
const VIEWPORT_MARGIN = 8;
const DEFAULT_PANEL_GAP = 4;
const ICON_ONLY_PANEL_GAP = 6;
/** Preferred list height for default variant; still clamped to available viewport space. */
const DEFAULT_PREFERRED_MAX_HEIGHT = 560;
const MIN_PANEL_HEIGHT = 160;

type MenuPos = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
};

type AppSelectDropdownProps = {
  value: string;
  onChange: (value: string) => void;
  options: AppSelectOption[];
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  /** z. B. kleineres / blasses Chevron für dezente Trigger */
  chevronClassName?: string;
  /** Optionales Icon links im Trigger, auch wenn nichts ausgewählt ist. */
  triggerIcon?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  /**
   * Nur Symbole in Trigger und Liste; `label` dient als aria-label / title (Barrierefreiheit).
   */
  variant?: 'default' | 'icon-only';
  /** Suchfeld in der geöffneten Liste (sinnvoll bei langen Optionenlisten). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Optionaler „Neu erstellen“-Eintrag unter dem Suchfeld (wie Kontakt-/Projektauswahl). */
  onCreate?: () => void;
  createLabel?: string;
  createIcon?: React.ReactNode;
  /** z-index für portalierte Liste (z. B. in Modals über plantafelDrawerModal). */
  menuZIndex?: number;
};

/**
 * Dropdown im App-Look (Rahmen, Schatten, Liste gestylt).
 * Liste per Portal + fixed, damit sie nicht von overflow-y-auto der Eltern abgeschnitten wird.
 */
export function AppSelectDropdown({
  value,
  onChange,
  options,
  placeholder = 'Bitte wählen …',
  className = '',
  triggerClassName = '',
  chevronClassName = '',
  triggerIcon,
  disabled = false,
  id,
  variant = 'default',
  searchable = false,
  searchPlaceholder = 'Suchen…',
  onCreate,
  createLabel = 'Neu erstellen',
  createIcon,
  menuZIndex = UI_Z_INDEX.portalDropdown,
}: AppSelectDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [searchAutofillLocked, setSearchAutofillLocked] = React.useState(true);
  const [menuPos, setMenuPos] = React.useState<MenuPos | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const searchFieldId = React.useId();

  const valueSafe = String(value ?? '');

  const selectedOption = React.useMemo(
    () => options.find((o) => o.value === valueSafe),
    [options, valueSafe]
  );
  const selectedLabel = selectedOption?.label ?? placeholder;
  const selectedIcon = selectedOption?.icon;
  const iconOnly = variant === 'icon-only';

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      setSearchAutofillLocked(true);
      return;
    }
    if (!searchable) return;
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, searchable]);

  const filteredOptions = React.useMemo(() => {
    if (!searchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.value === '' ||
        o.label.toLowerCase().includes(q) ||
        (o.description ?? '').toLowerCase().includes(q) ||
        (o.searchText ?? '').toLowerCase().includes(q),
    );
  }, [options, query, searchable]);

  const listOptions = searchable ? filteredOptions : options;
  const showSearchField = searchable && !iconOnly;
  const showCreateAction = Boolean(onCreate) && !iconOnly;

  const optionsHaveIcons = React.useMemo(() => options.some((o) => Boolean(o.icon)), [options]);

  const createActionButton =
    showCreateAction && onCreate ? (
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setOpen(false);
          onCreate();
        }}
        className="flex w-full shrink-0 items-center gap-2 border-b border-app-border px-3 py-2.5 text-left text-sm font-medium text-pipedrive-blue hover:bg-blue-50"
      >
        <span className="inline-flex shrink-0" aria-hidden>
          {createIcon ?? <Plus className="h-4 w-4" />}
        </span>
        {createLabel}
      </button>
    ) : null;

  const renderOption = (opt: AppSelectOption) => {
    const isSelected = valueSafe === opt.value;
    const key = opt.value === '' ? '__empty__' : opt.value;
    const ariaLabel = opt.description ? `${opt.label}, ${opt.description}` : opt.label;
    return (
      <li
        key={key}
        role="option"
        aria-selected={isSelected}
        aria-label={ariaLabel}
        title={iconOnly ? opt.label : undefined}
        data-app-select-value={opt.value}
        onMouseDown={(e) => {
          e.preventDefault();
          onChange(opt.value);
          setOpen(false);
        }}
        className={
          'cursor-pointer text-sm transition-colors ' +
          (iconOnly
            ? 'flex aspect-square min-h-[2.5rem] items-center justify-center rounded-lg border border-transparent ' +
              (isSelected
                ? 'bg-app-surface-muted text-app-text ring-1 ring-slate-300'
                : 'text-app-text-muted hover:bg-app-hover hover:border-app-border')
            : 'flex items-center gap-2.5 px-3 py-2.5 ' +
              (isSelected
                ? 'bg-blue-50 font-medium text-pipedrive-blue'
                : 'text-app-text-muted hover:bg-app-hover'))
        }
      >
        {iconOnly ? (
          opt.icon ? (
            <span className="inline-flex shrink-0 text-app-text-muted" aria-hidden>
              {opt.icon}
            </span>
          ) : (
            <span className="max-w-[8rem] truncate text-xs text-app-text-subtle">{opt.label}</span>
          )
        ) : (
          <>
            {opt.icon ? (
              <span className="inline-flex shrink-0 text-app-text-muted" aria-hidden>
                {opt.icon}
              </span>
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate">{opt.label}</span>
              {opt.description ? (
                <span className="mt-0.5 block truncate text-xs font-normal text-slate-400">
                  {opt.description}
                </span>
              ) : null}
            </span>
            {isSelected ? (
              <Check className="h-4 w-4 shrink-0 text-pipedrive-blue" aria-hidden />
            ) : (
              <span className="h-4 w-4 shrink-0" aria-hidden />
            )}
          </>
        )}
      </li>
    );
  };

  const groupedList = React.useMemo(() => {
    const groups: Array<{ label: string | null; options: AppSelectOption[] }> = [];
    for (const opt of listOptions) {
      const label = iconOnly ? null : opt.group ?? null;
      const last = groups[groups.length - 1];
      if (last && last.label === label) {
        last.options.push(opt);
      } else {
        groups.push({ label, options: [opt] });
      }
    }
    return groups;
  }, [iconOnly, listOptions]);

  const updateMenuPosition = React.useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = iconOnly ? ICON_ONLY_PANEL_GAP : DEFAULT_PANEL_GAP;
    const preferredMax = iconOnly
      ? Math.min(22 * 16, window.innerHeight * 0.7)
      : Math.min(DEFAULT_PREFERRED_MAX_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2);
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN - gap;
    const spaceAbove = rect.top - VIEWPORT_MARGIN - gap;
    const opensAbove =
      spaceBelow < Math.min(preferredMax, 160) && spaceAbove > spaceBelow;
    const available = opensAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(MIN_PANEL_HEIGHT, Math.min(preferredMax, available));

    const panelWidth = iconOnly
      ? ICON_ONLY_PANEL_WIDTH
      : optionsHaveIcons
        ? Math.max(rect.width, 240)
        : rect.width;
    let left = rect.left;
    if (left + panelWidth > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, rect.right - panelWidth);
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    if (opensAbove) {
      setMenuPos({
        bottom: window.innerHeight - rect.top + gap,
        left,
        width: panelWidth,
        maxHeight,
      });
      return;
    }
    setMenuPos({
      top: rect.bottom + gap,
      left,
      width: panelWidth,
      maxHeight,
    });
  }, [iconOnly, optionsHaveIcons]);

  React.useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const openedAt = Date.now();
    /** Kurz nach dem Öffnen Scroll-Events ignorieren (sonst schließt overflow-y-auto der Eltern sofort). */
    const onScroll = (e: Event) => {
      if (Date.now() - openedAt < 150) return;
      const t = e.target as Node | null;
      if (!t) return;
      if (listRef.current && (listRef.current === t || listRef.current.contains(t))) return;
      setOpen(false);
    };
    const onResize = () => updateMenuPosition();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updateMenuPosition]);

  React.useLayoutEffect(() => {
    if (!open || !menuPos || iconOnly || !valueSafe) return;
    const root = listRef.current;
    if (!root) return;
    const selected = root.querySelector(`[data-app-select-value="${CSS.escape(valueSafe)}"]`);
    if (!(selected instanceof HTMLElement)) return;
    const scroller =
      (selected.closest('[data-app-select-scroll]') as HTMLElement | null) ?? root;
    const offset = selected.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    scroller.scrollTop += offset;
  }, [iconOnly, menuPos, open, valueSafe]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || listRef.current?.contains(t)) return;
      /** Scrollbar-Klicks: Target ist manchmal nicht im UL-Baum */
      if ((e.target as HTMLElement)?.closest?.('[data-app-select-list]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const listContent =
    open && menuPos ? (
      <div
        ref={listRef}
        data-app-select-list
        style={{
          position: 'fixed',
          top: menuPos.top,
          bottom: menuPos.bottom,
          left: menuPos.left,
          width: menuPos.width,
          maxHeight: menuPos.maxHeight,
          zIndex: menuZIndex,
        }}
        data-app-select-scroll={iconOnly || showSearchField || showCreateAction ? undefined : ''}
        className={
          iconOnly
            ? 'grid grid-cols-4 gap-3 p-4 min-h-0 overflow-y-auto overscroll-y-contain rounded-xl border border-app-border bg-app-surface shadow-lg touch-pan-y'
            : showSearchField || showCreateAction
              ? 'flex min-h-0 flex-col overflow-hidden overscroll-y-contain rounded-lg border border-app-border bg-app-surface shadow-lg touch-pan-y'
              : 'min-h-0 overflow-y-auto overscroll-y-contain rounded-lg border border-app-border bg-app-surface py-1 shadow-lg touch-pan-y'
        }
        onWheel={(e) => e.stopPropagation()}
      >
        {showSearchField ? (
          <div className="shrink-0 border-b border-app-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-app-border bg-app-bg px-2.5 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
              <input
                ref={searchRef}
                id={searchFieldId}
                name={`app-select-search-${searchFieldId}`}
                type="text"
                role="searchbox"
                aria-label={searchPlaceholder}
                {...FILTER_INPUT_AUTOFILL_ATTRS}
                readOnly={searchAutofillLocked}
                onFocus={(e) => {
                  unlockFilterInputAfterFocus(e.currentTarget);
                  setSearchAutofillLocked(false);
                }}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                onMouseDown={(e) => e.stopPropagation()}
                className="min-w-0 flex-1 border-0 bg-transparent text-xs text-app-text-muted outline-none placeholder:text-slate-400"
              />
            </div>
          </div>
        ) : null}
        {createActionButton}
        {showSearchField || showCreateAction ? (
          <ul role="listbox" data-app-select-scroll className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain py-1">
            {listOptions.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-slate-400">Keine Treffer</li>
            ) : (
              groupedList.map((group, groupIndex) => (
                <React.Fragment key={`${group.label ?? 'ungrouped'}-${groupIndex}`}>
                  {group.label ? (
                    <li className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {group.label}
                    </li>
                  ) : null}
                  {group.options.map(renderOption)}
                </React.Fragment>
              ))
            )}
          </ul>
        ) : (
          <ul role="listbox" className="contents">
            {groupedList.map((group) => group.options.map(renderOption))}
          </ul>
        )}
      </div>
    ) : null;

  return (
    <div className={`relative min-w-0 ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={
          iconOnly
            ? valueSafe.trim() === ''
              ? placeholder
              : `Symbol: ${selectedLabel}`
            : undefined
        }
        onClick={() => !disabled && setOpen((o) => !o)}
        className={[
          'flex w-full min-w-0 items-center rounded-lg border border-app-border bg-app-surface text-left text-sm outline-none transition-colors hover:border-app-border-strong focus:border-pipedrive-blue focus:ring-2 focus:ring-pipedrive-blue/20 disabled:cursor-not-allowed disabled:opacity-60',
          valueSafe.trim() === '' && !selectedOption ? 'text-app-text-subtle' : 'text-app-text',
          iconOnly
            ? 'aspect-square justify-center p-0 min-h-[2.25rem]'
            : 'justify-between gap-2 px-3 py-2.5',
          triggerClassName,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {iconOnly ? (
          valueSafe.trim() !== '' && selectedIcon ? (
            <span className="inline-flex shrink-0 text-app-text-muted" aria-hidden>
              {selectedIcon}
            </span>
          ) : (
            <span
              className="h-4 w-4 shrink-0 rounded border border-dashed border-app-border-strong bg-app-surface-muted"
              aria-hidden
            />
          )
        ) : (
          <>
            <span className="min-w-0 flex-1 flex items-center gap-2">
              {valueSafe.trim() !== '' && selectedIcon ? (
                <span className="inline-flex shrink-0 text-app-text-muted" aria-hidden>
                  {selectedIcon}
                </span>
              ) : triggerIcon ? (
                <span className="inline-flex shrink-0" aria-hidden>
                  {triggerIcon}
                </span>
              ) : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate">
                  {selectedOption ? selectedLabel : placeholder}
                </span>
                {selectedOption?.description ? (
                  <span className="mt-0.5 block truncate text-xs font-normal text-slate-400">
                    {selectedOption.description}
                  </span>
                ) : null}
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-app-text-subtle transition-transform ${open ? 'rotate-180' : ''} ${chevronClassName}`.trim()}
              aria-hidden
            />
          </>
        )}
      </button>
      {listContent ? createPortal(listContent, document.body) : null}
    </div>
  );
}
